/*
 * Reading a MacroFactor export into what the import endpoint takes. Two workbooks come out of
 * that app — the data export, one sheet per category, and the food log, one row per thing
 * eaten — and this turns them into entries, foods, goals, weigh-ins, metrics and body stats
 * in our own shapes. Nothing here touches a file or the network: the workbook arrives already
 * read into sheets (see `xlsx.ts`), and what comes out is handed to the server as it is. The
 * Android app carries a port of this, `data/MacroFactor.kt`, held to the same test cases.
 *
 * What the export gives, and what it costs us:
 *
 *   Food log       one row per thing eaten: a day, a time, a name, an amount, and every
 *                  nutrient already worked out for that amount. The whole diary, and exact.
 *   Custom Foods   the account's own foods — but most are exported with every nutrient column
 *                  empty, and every recipe is here again as a food. Only the rows that carry
 *                  nutrients are worth a food of their own.
 *   Favorites      the same shape as the own foods; a favourite that carries nutrients is an
 *                  own food marked as one.
 *   Recipes        name, servings, total weight, whole-recipe nutrients, and ingredients as
 *                  text like `Milk (1 portion)`. The per-ingredient nutrients are not exported,
 *                  so a recipe cannot be rebuilt as one; it comes across as a food carrying
 *                  the totals MacroFactor recorded, which are true.
 *   Weight Goals   one row per goal ever followed: what it aimed at, the day it started and
 *                  ended, the weight it was going for and the rate it was going at.
 *   Nutrition Program Settings
 *                  the calories and macros the app coached with, seven weekday rows for every
 *                  day the program was updated on, and the expenditure it stood on. A goal's
 *                  targets are the program's as it stood on the day the goal started.
 *   Scale Weight, Body Metrics, Steps, User Profile
 *                  the body: weigh-ins, girths and the visual body fat assessment, steps a day,
 *                  and the stats the expenditure estimate needs.
 *
 * A food is not created for every name that was eaten. An entry keeps a copy of the food it
 * was filled in from, and both the search and the recently-logged list read foods off those
 * copies, so a name logged before is offered again whether or not it has a row. Every copy is
 * derived from the entry's own row rather than from one profile per name, because a food
 * edited in MacroFactor changes what it is worth from that day on and the old days keep what
 * they were logged at.
 */

import type { Cell, Sheet, Workbook } from './xlsx'

export type { Cell, Sheet, Workbook } from './xlsx'

// ── Our shapes, as the import endpoint takes them ────────────────────────────────────────

export type Nutrients = { calories: number } & Record<
  'protein' | 'carbs' | 'fat' | 'fibre' | 'sugar' | 'saturatedFat' | 'sodium',
  number | null
>

export const NUTRIENT_NAMES = [
  'calories',
  'protein',
  'carbs',
  'fat',
  'fibre',
  'sugar',
  'saturatedFat',
  'sodium',
] as const

export interface Portion {
  name: string
  amount: number
  unit: 'g' | 'ml'
}

export interface Amount {
  value: number
  unit: 'g' | 'ml' | 'portion'
  portion: string | null
}

/** A food as an entry carries a copy of one and as an own food is written. */
export interface Food {
  id: null
  source: 'own'
  libraryCode: null
  name: string
  brand: null
  barcode: null
  emoji: null
  sharing: 'private'
  reference: '100g' | '100ml' | 'portion'
  nutrients: Nutrients
  portions: Portion[]
  /** What a millilitre of it weighs, where the export says — see `densityOf`. */
  density: number | null
  recipe: null
}

/** One thing eaten. The key is the source's own, and settles the row's identifier. */
export interface ImportedEntry {
  key: string
  day: string
  time: string
  name: string
  food: Food
  amount: Amount
  nutrients: Nutrients
}

export interface ImportedFood {
  key: string
  favourite: boolean
  food: Food
}

export interface DayTargets {
  calories: number
  protein: number
  carbs: number
  fat: number
}

/**
 * A goal as it was followed, set by hand: the aim, the target and the rate from the goal, the
 * targets from the program as it stood when the goal started.
 */
export interface ImportedGoal {
  key: string
  effectiveFrom: string
  aim: 'lose' | 'maintain' | 'gain'
  targetWeightKg: number | null
  /** The rate in kilograms, which is what the percentage of the starting weight came to. */
  rateKgPerWeek: number | null
  calories: number
  protein: number
  carbs: number
  fat: number
  /** Seven days' targets, Monday first, where the week is not the same throughout. */
  dayTargets: DayTargets[] | null
  expenditureAtSet: number | null
  endedOn: string | null
  /** Still being followed when the export was taken. */
  inProgress: boolean
}

export type Measurement =
  'neck' | 'chest' | 'waist' | 'hips' | 'biceps' | 'thigh' | 'calf' | 'bodyFat'

export interface ImportedWeighIn {
  day: string
  weightKg: number | null
  /** Girths in metres, body fat as a ratio — as a weigh-in keeps them. */
  measurements: Partial<Record<Measurement, number>>
}

export interface ImportedMetric {
  day: string
  kind: 'steps'
  value: number
}

export interface ImportedProfile {
  birthDate?: string
  sex?: 'female' | 'male'
  heightM?: number
  activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active'
}

export interface ImportBody {
  source: string
  entries?: ImportedEntry[]
  foods?: ImportedFood[]
  goals?: ImportedGoal[]
  weighIns?: ImportedWeighIn[]
  metrics?: ImportedMetric[]
  profile?: ImportedProfile
}

export interface Skipped {
  /** Foods the export emptied of every nutrient, and which nothing eaten could fill in. */
  foodsWithoutNutrients: string[]
  /** Rows that named nothing, weighed nothing, or said no calories. */
  rows: number
  /** Recipes brought across as foods, having no per-ingredient nutrients to be rebuilt from. */
  recipesAsFoods: string[]
  /** Goals that started before any program stood, and so have no targets to carry. */
  goalsWithoutProgram: number
}

export interface MacroFactorImport {
  entries: ImportedEntry[]
  foods: ImportedFood[]
  goals: ImportedGoal[]
  weighIns: ImportedWeighIn[]
  metrics: ImportedMetric[]
  profile: ImportedProfile
  skipped: Skipped
}

// ── The files ────────────────────────────────────────────────────────────────────────────

export type MacroFactorFile = 'export' | 'log'

/** The two files a MacroFactor export is made of, in the order the process lists them. */
export const MACROFACTOR_FILES: { kind: MacroFactorFile; label: string }[] = [
  { kind: 'export', label: 'Data export' },
  { kind: 'log', label: 'Food log' },
]

export const SOURCE = { id: 'macrofactor', name: 'MacroFactor', files: MACROFACTOR_FILES }

export interface MacroFactorWorkbooks {
  export?: Workbook
  log?: Workbook
}

/**
 * Which of the two a workbook is, told by what its sheets hold rather than by its name — the
 * app names both after itself and the minute they were taken. The food log is one sheet
 * headed by a day, a time and a food name; the data export has the weight goals in it.
 */
export function kindOf(workbook: Workbook): MacroFactorFile | null {
  const names = new Set(workbook.sheets.map((sheet) => sheet.name))
  if (names.has('Weight Goals') || names.has('Custom Foods') || names.has('Scale Weight')) {
    return 'export'
  }
  const first = workbook.sheets[0]
  const header = new Set(headerOf(first))
  if (header.has('Date') && header.has('Time') && header.has('Food Name')) {
    return 'log'
  }
  return null
}

// ── Cells ────────────────────────────────────────────────────────────────────────────────

const MAX_TEXT = 200
const DAY_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/
/** `25/06/2024` — a day as the export writes one in text, day first. */
const TEXT_DAY_SHAPE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
const TIME_SHAPE = /^(\d{1,2}):([0-5]\d)$/
/** Excel counts days from the last day of 1899, and that day is this many days before 1970. */
const EXCEL_EPOCH_DAYS = 25569
const MS_PER_DAY = 86_400_000

type Row = Record<string, Cell>

function headerOf(sheet: Sheet | undefined): string[] {
  return (sheet?.rows[0] ?? []).map((cell) => (cell === null ? '' : String(cell).trim()))
}

/** A sheet's rows by column name, without the header and without rows holding nothing. */
export function recordsOf(sheet: Sheet | undefined): Row[] {
  if (sheet === undefined) {
    return []
  }
  const header = headerOf(sheet)
  return sheet.rows
    .slice(1)
    .filter((cells) => cells.some((cell) => cell !== null))
    .map((cells) => {
      const row: Row = {}
      header.forEach((name, at) => {
        if (name !== '') {
          row[name] = cells[at] ?? null
        }
      })
      return row
    })
}

function text(cell: Cell | undefined): string | null {
  if (cell === null || cell === undefined) {
    return null
  }
  const trimmed = String(cell).trim()
  return trimmed === '' ? null : trimmed.slice(0, MAX_TEXT)
}

function number(cell: Cell | undefined): number | null {
  if (cell === null || cell === undefined) {
    return null
  }
  const read = typeof cell === 'number' ? cell : Number(cell.trim())
  return Number.isFinite(read) ? read : null
}

/** Two decimal places, as every nutrient is kept everywhere else. */
function round(value: number): number {
  return Math.round(value * 100) / 100
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * A day as every client keeps one, `YYYY-MM-DD`, off whichever way the export wrote it: the
 * number Excel keeps a date as, a day-first text, or the ISO form the profile uses.
 */
export function dayOf(cell: Cell | undefined): string | null {
  if (cell === null || cell === undefined) {
    return null
  }
  if (typeof cell === 'number') {
    const date = new Date(Math.round((cell - EXCEL_EPOCH_DAYS) * MS_PER_DAY))
    if (Number.isNaN(date.getTime())) {
      return null
    }
    return `${String(date.getUTCFullYear())}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
  }
  const trimmed = cell.trim()
  if (DAY_SHAPE.test(trimmed)) {
    return trimmed
  }
  const said = TEXT_DAY_SHAPE.exec(trimmed)
  if (said === null) {
    return null
  }
  const [day, month, year] = [Number(said[1]), Number(said[2]), said[3] ?? '']
  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return null
  }
  return `${year}-${pad(month)}-${pad(day)}`
}

/** A time of day as every client keeps one, `HH:MM`, off a fraction of a day or a text. */
export function timeOf(cell: Cell | undefined): string | null {
  if (cell === null || cell === undefined) {
    return null
  }
  if (typeof cell === 'number') {
    if (cell < 0 || cell >= 1) {
      return null
    }
    const minutes = Math.round(cell * 1440) % 1440
    return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
  }
  const said = TIME_SHAPE.exec(cell.trim())
  if (said === null) {
    return null
  }
  const hour = Number(said[1])
  return hour > 23 ? null : `${pad(hour)}:${said[2] ?? '00'}`
}

/**
 * An identifier's worth of key, settled by what a record names rather than drawn at random,
 * so importing the same export twice writes the same rows twice instead of a second copy.
 */
function keyOf(...parts: string[]): string {
  return parts.join('|')
}

// ── Nutrients and amounts ────────────────────────────────────────────────────────────────

/** Which MacroFactor column each of our nutrients is in. Everything else it exports is dropped. */
const NUTRIENT_COLUMNS: Record<keyof Nutrients, string> = {
  calories: 'Calories (kcal)',
  protein: 'Protein (g)',
  carbs: 'Carbs (g)',
  fat: 'Fat (g)',
  fibre: 'Fiber (g)',
  sugar: 'Sugars (g)',
  saturatedFat: 'Saturated Fat (g)',
  sodium: 'Sodium (mg)',
}

/** The serving sizes that mean a weight or a volume rather than a portion of the food's own. */
const WEIGHT_UNITS = new Set(['g', 'gram', 'grams'])
const VOLUME_UNITS = new Set(['ml', 'millilitre', 'milliliter'])

/** The nutrients off a row: calories or nothing, and every other nutrient it happens to state. */
function nutrientsOf(row: Row, factor = 1): Nutrients | null {
  const calories = number(row[NUTRIENT_COLUMNS.calories])
  if (calories === null || calories < 0) {
    return null
  }
  const read: Record<string, number | null> = {}
  for (const [nutrient, column] of Object.entries(NUTRIENT_COLUMNS)) {
    const value = number(row[column])
    read[nutrient] = value === null || value < 0 ? null : round(value * factor)
  }
  read.calories = round(calories * factor)
  return read as unknown as Nutrients
}

/**
 * The same nutrients stated per a hundred, which is what a food carries. Kept to four places
 * rather than to the two every other figure takes: this one is divided by a serving and then
 * multiplied by one again, and two places is not enough to come back to what was eaten.
 */
function per(nutrients: Nutrients, measure: number): Nutrients {
  const factor = 100 / measure
  const out: Record<string, number | null> = {}
  for (const nutrient of NUTRIENT_NAMES) {
    const value = nutrients[nutrient]
    out[nutrient] = value === null ? null : Math.round(value * factor * 10_000) / 10_000
  }
  return out as unknown as Nutrients
}

interface Serving {
  amount: Amount
  /** What the amount comes to in the food's own units, which is what nutrients are per. */
  measure: number
  reference: Food['reference']
  portions: Portion[]
  density: number | null
}

/**
 * The density a row logged by volume states — the data spec: the weight column of a row whose
 * serving is a millilitre is what one millilitre of it weighs, which is the density itself.
 * Half a gram to one and a half; outside that the column is saying something other than a
 * liquid's weight, and a density read out of it would be worse than none.
 */
function densityOf(weight: number | null): number | null {
  if (weight === null || weight < 0.5 || weight > 1.5) {
    return null
  }
  return Math.round(weight * 1000) / 1000
}

/**
 * What a row's serving size, quantity and weight come to. MacroFactor states the quantity in
 * servings and the weight of one, so `6` and `33` is six scoops of thirty-three grams; for a
 * row logged by weight the quantity is the weight and the serving weighs one.
 */
function servingOf(row: Row): Serving | null {
  const size = text(row['Serving Size'])
  const quantity = number(row['Serving Qty'])
  const weight = number(row['Serving Weight (g)'])
  if (quantity === null || quantity <= 0) {
    return null
  }
  const named = (size ?? '').toLowerCase()
  // Logged by weight or by volume, the quantity is already the measure. The weight column
  // then says what one of those units weighs — one, for a gram; the density, for a millilitre
  // — and multiplying by it would turn a glass of vodka into the grams of vodka in it.
  if (size === null || WEIGHT_UNITS.has(named)) {
    return {
      amount: { value: quantity, unit: 'g', portion: null },
      measure: quantity,
      reference: '100g',
      portions: [],
      density: null,
    }
  }
  if (VOLUME_UNITS.has(named)) {
    return {
      amount: { value: quantity, unit: 'ml', portion: null },
      measure: quantity,
      reference: '100ml',
      portions: [],
      density: densityOf(weight),
    }
  }
  if (weight === null || weight <= 0) {
    return null
  }
  // Anything else the app called it — a scoop, a slice, a large egg — is a portion, and the
  // weight the export gives is what one of them weighs.
  return {
    amount: { value: quantity, unit: 'portion', portion: size },
    measure: quantity * weight,
    reference: '100g',
    portions: [{ name: size, amount: weight, unit: 'g' }],
    density: null,
  }
}

function foodOf(
  name: string,
  reference: Food['reference'],
  nutrients: Nutrients,
  portions: Portion[],
  density: number | null,
): Food {
  return {
    id: null,
    source: 'own',
    libraryCode: null,
    name,
    brand: null,
    barcode: null,
    emoji: null,
    sharing: 'private',
    reference,
    nutrients,
    portions,
    density,
    recipe: null,
  }
}

// ── The diary ────────────────────────────────────────────────────────────────────────────

/** The food log: a day's worth of rows apiece, each already worked out. */
function readEntries(sheet: Sheet | undefined, skipped: Skipped): ImportedEntry[] {
  const entries: ImportedEntry[] = []
  // How many rows of a day have already named this food at this time, so that two identical
  // helpings an hour apart keep two settled keys rather than colliding onto one.
  const seen = new Map<string, number>()
  for (const row of recordsOf(sheet)) {
    const name = text(row['Food Name'])
    const day = dayOf(row.Date)
    const time = timeOf(row.Time)
    const serving = servingOf(row)
    const nutrients = nutrientsOf(row)
    if (name === null || day === null || time === null || serving === null || nutrients === null) {
      skipped.rows += 1
      continue
    }
    const named = keyOf(day, time, name)
    const ordinal = seen.get(named) ?? 0
    seen.set(named, ordinal + 1)
    entries.push({
      key: keyOf(named, String(serving.amount.value), String(ordinal)),
      day,
      time,
      name,
      // The food as it stood on the day it was eaten, worked back out of what it came to.
      food: foodOf(
        name,
        serving.reference,
        per(nutrients, serving.measure),
        serving.portions,
        serving.density,
      ),
      amount: serving.amount,
      nutrients,
    })
  }
  // Oldest first, so a run that stops halfway has left a diary that starts at the beginning.
  entries.sort((a, b) =>
    a.day === b.day ? a.time.localeCompare(b.time) : a.day.localeCompare(b.day),
  )
  return entries
}

// ── Foods ────────────────────────────────────────────────────────────────────────────────

/** The own foods, or the favourites: the rows that carry nutrients, which are worth keeping. */
function readFoods(sheet: Sheet | undefined, favourite: boolean, skipped: Skipped): ImportedFood[] {
  const foods: ImportedFood[] = []
  for (const row of recordsOf(sheet)) {
    const name = text(row['Food Name'])
    if (name === null) {
      skipped.rows += 1
      continue
    }
    const serving = servingOf(row)
    const nutrients = nutrientsOf(row)
    if (serving === null || nutrients === null) {
      // MacroFactor exports most own foods with every nutrient column empty. Nothing can be
      // made of those, and the entries they were logged in carry their nutrients anyway.
      skipped.foodsWithoutNutrients.push(name)
      continue
    }
    foods.push({
      key: keyOf('f', name),
      favourite,
      food: foodOf(
        name,
        serving.reference,
        per(nutrients, serving.measure),
        serving.portions,
        serving.density,
      ),
    })
  }
  return foods
}

/**
 * The recipes, as foods. Their nutrients are the whole recipe's, so one serving is them
 * divided by the servings it makes, and the serving is a portion weighing its share of the
 * total. The ingredients have no nutrients of their own in the export, and a recipe whose
 * ingredients do not add up to its total is a worse thing to store than a food that never
 * claimed to have any.
 */
function readRecipes(sheet: Sheet | undefined, skipped: Skipped): ImportedFood[] {
  const foods: ImportedFood[] = []
  for (const row of recordsOf(sheet)) {
    const name = text(row['Recipe Name'])
    const servings = number(row['Serving Qty'])
    const whole = nutrientsOf(row)
    if (name === null || servings === null || servings <= 0 || whole === null) {
      skipped.rows += 1
      continue
    }
    const total = number(row['Total Weight (g)'])
    const perServing = nutrientsOf(row, 1 / servings)
    if (perServing === null) {
      skipped.rows += 1
      continue
    }
    // A recipe whose total weight the export left out still has to be logged by something, and
    // a serving of it is the only amount anybody ever means.
    const weight = total !== null && total > 0 ? round(total / servings) : null
    foods.push({
      key: keyOf('r', name),
      favourite: false,
      food: foodOf(
        name,
        'portion',
        perServing,
        [{ name: 'serving', amount: weight ?? 1, unit: 'g' }],
        null,
      ),
    })
    skipped.recipesAsFoods.push(name)
  }
  return foods
}

// ── Goals ────────────────────────────────────────────────────────────────────────────────

/** What MacroFactor calls a goal, and what we aim at. */
const AIMS: Record<string, ImportedGoal['aim']> = {
  'weight loss': 'lose',
  maintenance: 'maintain',
  'weight gain': 'gain',
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

/** The program as it stood from one day on: seven days' targets and the expenditure. */
interface Program {
  from: string
  days: DayTargets[]
  expenditure: number | null
}

/**
 * The programs, oldest first. Every update writes seven weekday rows; a weekday the update
 * left out takes the mean of the ones it gave, so a program is always seven days long.
 */
function readPrograms(sheet: Sheet | undefined): Program[] {
  const byDay = new Map<string, { days: (DayTargets | null)[]; expenditure: number | null }>()
  for (const row of recordsOf(sheet)) {
    const from = dayOf(row['Program Update Date'])
    const weekday = WEEKDAYS.indexOf((text(row['Program Weekday']) ?? '').toLowerCase())
    const calories = number(row['Calories (kcal)'])
    const protein = number(row['Protein (g)'])
    const carbs = number(row['Carbs (g)'])
    const fat = number(row['Fat (g)'])
    if (
      from === null ||
      weekday === -1 ||
      calories === null ||
      protein === null ||
      carbs === null ||
      fat === null
    ) {
      continue
    }
    const program = byDay.get(from) ?? {
      days: Array<DayTargets | null>(7).fill(null),
      expenditure: null,
    }
    program.days[weekday] = { calories, protein, carbs, fat }
    program.expenditure = number(row['Expenditure (kcal)']) ?? program.expenditure
    byDay.set(from, program)
  }
  const programs: Program[] = []
  for (const [from, program] of byDay) {
    const given = program.days.filter((day): day is DayTargets => day !== null)
    if (given.length === 0) {
      continue
    }
    const mean = meanOf(given)
    programs.push({
      from,
      days: program.days.map((day) => day ?? mean),
      expenditure: program.expenditure,
    })
  }
  return programs.sort((a, b) => a.from.localeCompare(b.from))
}

/** The seven days' targets averaged, rounded to the whole. */
function meanOf(days: readonly DayTargets[]): DayTargets {
  const sum = (name: keyof DayTargets) => days.reduce((total, day) => total + day[name], 0)
  return {
    calories: Math.round(sum('calories') / days.length),
    protein: Math.round(sum('protein') / days.length),
    carbs: Math.round(sum('carbs') / days.length),
    fat: Math.round(sum('fat') / days.length),
  }
}

function sameAllWeek(days: readonly DayTargets[]): boolean {
  const first = days[0]
  return (
    first !== undefined &&
    days.every(
      (day) =>
        day.calories === first.calories &&
        day.protein === first.protein &&
        day.carbs === first.carbs &&
        day.fat === first.fat,
    )
  )
}

/**
 * The goals, oldest first, each set by hand with the targets the program coached with on the
 * day it started — and the goal still in progress with the program as it last stood. A goal
 * is in force until the next one starts, which is how a goal is kept here too, so an end date
 * matters only on the last goal, where nothing follows it.
 *
 * The rate is a percentage of body weight a week, and ours is a weight a week, so it is read
 * against the weight the goal started from. A goal to hold a weight is going nowhere at any
 * rate, and keeps none.
 */
function readGoals(
  goals: Sheet | undefined,
  programs: readonly Program[],
  skipped: Skipped,
): ImportedGoal[] {
  const byDay = new Map<string, ImportedGoal>()
  const rows = recordsOf(goals)
  rows.forEach((row, at) => {
    const aim = AIMS[(text(row.Goal) ?? '').toLowerCase()]
    const effectiveFrom = dayOf(row['Start Date'])
    if (aim === undefined || effectiveFrom === null) {
      skipped.rows += 1
      return
    }
    const inProgress = (text(row.Status) ?? '').toLowerCase() === 'in progress'
    const program = inProgress
      ? programs[programs.length - 1]
      : (programs.filter((each) => each.from <= effectiveFrom).pop() ?? programs[0])
    if (program === undefined) {
      skipped.goalsWithoutProgram += 1
      return
    }
    const startingWeightKg =
      number(row['Starting Trend Weight (kg)']) ?? number(row['Starting Scale Weight (kg)'])
    const percent = number(row['Goal Rate per Week (%)'])
    const rateKgPerWeek =
      aim === 'maintain' || percent === null || startingWeightKg === null
        ? null
        : round((percent / 100) * startingWeightKg)
    const last = at === rows.length - 1
    const endedOn = !inProgress && last ? dayOf(row['End Date']) : null
    const mean = meanOf(program.days)
    byDay.set(effectiveFrom, {
      key: keyOf('g', effectiveFrom),
      effectiveFrom,
      aim,
      targetWeightKg: number(row['Goal Weight (kg)']),
      rateKgPerWeek,
      ...mean,
      dayTargets: sameAllWeek(program.days) ? null : program.days.map((day) => ({ ...day })),
      expenditureAtSet: program.expenditure,
      endedOn,
      inProgress,
    })
  })
  return [...byDay.values()].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
}

// ── The body ─────────────────────────────────────────────────────────────────────────────

const KG_PER_LB = 0.45359237
const CM_PER_IN = 2.54

/** A column by what it measures, whichever unit the export wrote it in, converted to ours. */
function measured(row: Row, name: string, metric: 'kg' | 'cm'): number | null {
  const own = number(row[`${name} (${metric})`])
  if (own !== null) {
    return own
  }
  const imperial = number(row[`${name} (${metric === 'kg' ? 'lb' : 'in'})`])
  if (imperial === null) {
    return null
  }
  return imperial * (metric === 'kg' ? KG_PER_LB : CM_PER_IN)
}

/** The girths a weigh-in keeps, and the export's columns for each — two where it splits sides. */
const GIRTHS: { name: Exclude<Measurement, 'bodyFat'>; columns: string[] }[] = [
  { name: 'neck', columns: ['Neck'] },
  { name: 'chest', columns: ['Chest'] },
  { name: 'waist', columns: ['Waist'] },
  { name: 'hips', columns: ['Hips'] },
  { name: 'biceps', columns: ['Left Bicep', 'Right Bicep'] },
  { name: 'thigh', columns: ['Left Thigh', 'Right Thigh'] },
  { name: 'calf', columns: ['Left Calf', 'Right Calf'] },
]

/** A ratio out of a percentage, or out of a ratio already — the export has written both. */
function ratioOf(value: number | null): number | null {
  if (value === null || value <= 0) {
    return null
  }
  return Math.round((value > 1 ? value / 100 : value) * 10_000) / 10_000
}

/**
 * The weigh-ins and the body metrics, one weigh-in a day. A day with both a scale body fat
 * and a visual assessment takes the scale's; a girth given for both sides is the mean of the
 * two, in metres, to the millimetre.
 */
function readWeighIns(weights: Sheet | undefined, metrics: Sheet | undefined): ImportedWeighIn[] {
  const byDay = new Map<string, ImportedWeighIn>()
  const on = (day: string): ImportedWeighIn => {
    const found = byDay.get(day) ?? { day, weightKg: null, measurements: {} }
    byDay.set(day, found)
    return found
  }
  for (const row of recordsOf(metrics)) {
    const day = dayOf(row.Date)
    if (day === null) {
      continue
    }
    const weighIn = on(day)
    for (const girth of GIRTHS) {
      const sides = girth.columns
        .map((column) => measured(row, column, 'cm'))
        .filter((value): value is number => value !== null && value > 0)
      if (sides.length > 0) {
        const cm = sides.reduce((total, side) => total + side, 0) / sides.length
        weighIn.measurements[girth.name] = Math.round(cm * 10) / 1000
      }
    }
    const visual = ratioOf(number(row['Visual Body Fat Assessment']))
    if (visual !== null) {
      weighIn.measurements.bodyFat = visual
    }
  }
  for (const row of recordsOf(weights)) {
    const day = dayOf(row.Date)
    if (day === null) {
      continue
    }
    const weighIn = on(day)
    const weight = measured(row, 'Weight', 'kg')
    if (weight !== null && weight > 0) {
      weighIn.weightKg = round(weight)
    }
    const fat = ratioOf(number(row['Fat Percent']))
    if (fat !== null) {
      weighIn.measurements.bodyFat = fat
    }
  }
  return [...byDay.values()]
    .filter((weighIn) => weighIn.weightKg !== null || Object.keys(weighIn.measurements).length > 0)
    .sort((a, b) => a.day.localeCompare(b.day))
}

/** The steps a day, where the day counted any. */
function readSteps(sheet: Sheet | undefined): ImportedMetric[] {
  const metrics: ImportedMetric[] = []
  for (const row of recordsOf(sheet)) {
    const day = dayOf(row.Date)
    const steps = number(row.Steps)
    if (day === null || steps === null || steps <= 0) {
      continue
    }
    metrics.push({ day, kind: 'steps', value: Math.round(steps) })
  }
  return metrics.sort((a, b) => a.day.localeCompare(b.day))
}

/** What MacroFactor calls an activity level, and what we call it. */
const ACTIVITY_LEVELS: Record<string, ImportedProfile['activityLevel']> = {
  sedentary: 'sedentary',
  'lightly active': 'light',
  'moderately active': 'moderate',
  'very active': 'active',
  'extremely active': 'active',
}

/** The body stats off the profile: each only where the export said something readable. */
function readProfile(sheet: Sheet | undefined): ImportedProfile {
  const row = recordsOf(sheet)[0]
  const profile: ImportedProfile = {}
  if (row === undefined) {
    return profile
  }
  const sex = (text(row.Sex) ?? '').toLowerCase()
  if (sex === 'male' || sex === 'female') {
    profile.sex = sex
  }
  const birthDate = dayOf(row.Birthday)
  if (birthDate !== null) {
    profile.birthDate = birthDate
  }
  const height = measured(row, 'Height', 'cm')
  if (height !== null && height > 0) {
    profile.heightM = Math.round(height * 10) / 1000
  }
  const level = ACTIVITY_LEVELS[(text(row['Basic Activity Level']) ?? '').toLowerCase()]
  if (level !== undefined) {
    profile.activityLevel = level
  }
  return profile
}

// ── The reading ──────────────────────────────────────────────────────────────────────────

function sheetOf(workbook: Workbook | undefined, name: string): Sheet | undefined {
  return workbook?.sheets.find((sheet) => sheet.name === name)
}

/**
 * A MacroFactor export read into everything the import takes. Either workbook may be
 * missing; the log carries everything eaten, and the export carries the rest.
 */
export function readMacroFactor(workbooks: MacroFactorWorkbooks): MacroFactorImport {
  const skipped: Skipped = {
    foodsWithoutNutrients: [],
    rows: 0,
    recipesAsFoods: [],
    goalsWithoutProgram: 0,
  }
  const entries = readEntries(workbooks.log?.sheets[0], skipped)
  const own = readFoods(sheetOf(workbooks.export, 'Custom Foods'), false, skipped)
  const favourites = readFoods(sheetOf(workbooks.export, 'Favorites'), true, skipped)
  const recipes = readRecipes(sheetOf(workbooks.export, 'Recipes'), skipped)

  // A recipe is exported as a food as well as a recipe, and a favourite may be an own food
  // too. The recipe is the fuller of the two — it knows what the whole weighs and how many
  // servings it makes — so it wins the name; a favourite marks whichever food holds it.
  const byName = new Map<string, ImportedFood>()
  for (const food of [...own, ...favourites, ...recipes]) {
    const name = food.food.name.toLowerCase()
    const held = byName.get(name)
    const favourite = food.favourite || (held?.favourite ?? false)
    // An own food and a favourite are the same shape, so the first read stands; a recipe
    // replaces either.
    const chosen = held !== undefined && food.key.startsWith('f|') ? held : food
    byName.set(name, { ...chosen, favourite })
  }
  const foods = [...byName.values()]
  skipped.foodsWithoutNutrients = [
    ...new Set(skipped.foodsWithoutNutrients.filter((name) => !byName.has(name.toLowerCase()))),
  ]
  skipped.recipesAsFoods = [
    ...new Map(
      skipped.recipesAsFoods
        .filter((name) => byName.has(name.toLowerCase()))
        .map((name) => [name.toLowerCase(), name]),
    ).values(),
  ]

  const programs = readPrograms(sheetOf(workbooks.export, 'Nutrition Program Settings'))
  const goals = readGoals(sheetOf(workbooks.export, 'Weight Goals'), programs, skipped)
  const weighIns = readWeighIns(
    sheetOf(workbooks.export, 'Scale Weight'),
    sheetOf(workbooks.export, 'Body Metrics'),
  )
  const metrics = readSteps(sheetOf(workbooks.export, 'Steps'))
  const profile = readProfile(sheetOf(workbooks.export, 'User Profile'))
  return { entries, foods, goals, weighIns, metrics, profile, skipped }
}
